import type { BadgeKind } from './StateBadge';

/**
 * `case_states` → badge kind, assigned **once** for the whole console.
 *
 * The design system is explicit that this mapping is settled in one place «so
 * no surface picks its own — leaving it to each pack is how two consoles end up
 * colouring the same state differently». Three surfaces read it: the report
 * queue, the case queue, and the account detail's case history. It lives in the
 * shared ring for that reason and not because three features happened to want
 * the same helper.
 *
 * **This is a presentation map, not the vocabulary.** `CASE_STATES` is a
 * `support__configurations` value the console never compiles in as a set — the
 * queue reads it from `GET /api/v1/caseStates` and every row carries its own
 * token. What is keyed here is how a token the design system has already
 * assigned a badge to is drawn.
 *
 * `open` takes the accent because it is the one state that is **work waiting on
 * the operator reading the row** — the accent's single sanctioned meaning on this
 * console. `awaiting user` is a warning because the transition is someone else's;
 * `resolved` is a success; `closed` is neutral because it is terminal and
 * unremarkable, and a success there would read as a second, better resolution.
 *
 * **All four are distinguishable, and that is the point of the map.** `open` and
 * `closed` were both neutral, so the two states an operator most needs to tell
 * apart — the queue's work and the queue's residue — drew the identical capsule,
 * on a queue that runs across all four states rather than filtering among them.
 * The label was carrying the whole distinction; colour now carries it too, and
 * still never alone.
 *
 * **A value with no row degrades — it does not crash and it does not vanish.**
 * The badge taxonomy is closed while the setting is not, so a fifth configured
 * state renders `neutral` carrying its literal token until the design system
 * assigns it one. That ordering is a release-planning fact, stated in the
 * catalog, in the design system, and in the admin pack.
 *
 * **The label is not here.** Each surface owns its own wording — «بانتظار
 * المستخدم» in the case queue and «بانتظار المُبلِّغ» on the report surfaces
 * are one value read twice, and a shared label map would make that
 * impossible.
 */
const CASE_STATE_BADGE: Readonly<Record<string, BadgeKind>> = {
  open: 'accent',
  'awaiting user': 'warning',
  resolved: 'success',
  closed: 'neutral',
};

export function caseStateBadgeKind(state: string): BadgeKind {
  return CASE_STATE_BADGE[state] ?? 'neutral';
}
